import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminDataToolbar } from "@/components/admin/import/AdminDataToolbar";
import { RefreshBar } from "@/components/admin/RefreshBar";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { getReturnsBoard } from "@/lib/admin/production.functions";
import { addDays, istToday } from "@/lib/admin/production.schemas";

export const Route = createFileRoute("/_admin/admin/returns")({
  head: () => ({
    meta: [
      { title: "Returns / Refunds — Admin" },
      {
        name: "description",
        content: "Refund pending, refund completed, cancelled and returned orders.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ReturnsPage,
});

function inr(paise: number | null): string {
  if (paise == null) return "—";
  return `₹${(paise / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function ReturnsPage() {
  const [from, setFrom] = useState(addDays(istToday(), -30));
  const [to, setTo] = useState(addDays(istToday(), 7));
  const [filter, setFilter] = useState("");

  const listFn = useServerFn(getReturnsBoard);
  const board = useQuery({
    queryKey: ["admin", "returns", from, to],
    queryFn: () => listFn({ data: { from, to } }),
    staleTime: 10_000,
    placeholderData: (prev) => prev,
  });

  const all = board.data ?? [];
  const refundPending = all.filter(
    (r) => r.status !== "refunded" && (r.payment_status === "paid" || r.payment_status === "captured"),
  );
  const refundCompleted = all.filter((r) => r.status === "refunded");
  const cancelled = all.filter((r) => r.status === "cancelled");
  const returned = all.filter((r) => r.delivery_state === "returned");

  const buckets: { key: string; label: string; rows: typeof all }[] = [
    { key: "refund_pending", label: "Refund pending", rows: refundPending },
    { key: "refund_completed", label: "Refund completed", rows: refundCompleted },
    { key: "cancelled", label: "Cancelled orders", rows: cancelled },
    { key: "returned", label: "Returned orders", rows: returned },
  ];

  const rows = filter ? (buckets.find((b) => b.key === filter)?.rows ?? []) : all;

  return (
    <div>
      <AdminPageHeader
        title="Returns / Refunds"
        description="Cancelled, failed and refunded orders with their settlement state."
        actions={
          <AdminDataToolbar
            exportName="returns-refunds"
            moduleId="returns-refunds"
            onImported={() => void board.refetch()}
            rows={() =>
              rows.map((r) => ({
                "Reference No": r.order_ref,
                "Order ID": r.id,
                "Amount (paise)": r.payment_amount_paise ?? r.total_amount_paise ?? 0,
                Method: r.razorpay_payment_id ? "razorpay" : "wallet",
                Status: r.status,
                Reason: "",
                Notes: "",
              }))
            }
          />
        }
      />


      <RefreshBar
        targets={[{ key: "returns", label: "returns" }]}
        busy={board.isFetching}
        lastUpdated={board.dataUpdatedAt || null}
      />

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {buckets.map((b) => (
          <button
            key={b.key}
            type="button"
            onClick={() => setFilter(filter === b.key ? "" : b.key)}
            className={`rounded-lg border p-3 text-left transition-colors ${
              filter === b.key ? "border-primary bg-muted" : "border-border hover:bg-muted/50"
            }`}
          >
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {b.label}
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums">{b.rows.length}</p>
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value || from)}
          className="h-9 w-40"
          aria-label="From date"
        />
        <Input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value || to)}
          className="h-9 w-40"
          aria-label="To date"
        />
      </div>

      {board.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
          Nothing in this bucket for the selected range.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Order</th>
                <th className="px-3 py-2 text-left">Customer</th>
                <th className="px-3 py-2 text-left">Delivery date</th>
                <th className="px-3 py-2 text-left">Order status</th>
                <th className="px-3 py-2 text-left">Payment status</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-left">Gateway payment</th>
                <th className="px-3 py-2 text-left">Updated</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2 font-mono text-xs">{r.order_ref}</td>
                  <td className="px-3 py-2">{r.customer_name}</td>
                  <td className="px-3 py-2">{r.preferred_delivery_date}</td>
                  <td className="px-3 py-2">{r.status.replace(/_/g, " ")}</td>
                  <td className="px-3 py-2">{r.payment_status ?? "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {inr(r.total_amount_paise ?? r.payment_amount_paise)}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{r.razorpay_payment_id ?? "—"}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {r.updated_at ? new Date(r.updated_at).toLocaleString("en-IN") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
